// UBuilder Lite — autosave dirty-flag middleware.
//
// Watches for user-driven actions that mutate persisted state and dispatches
// MARK_LITE_DIRTY. UBuilderLiteContainer.maybeAutoSave reads state.app.liteDirty
// and skips the PUT when false, so an idle user no longer hammers the server
// every 5 seconds. The flag is cleared on UPDATE_LITE_PROJECT_SUCCESS (handled
// in appReducer.ts) so a successful save naturally rearms.
//
// IMPORTANT: this is a whitelist. If you add a new user-facing action that
// changes anything that should be saved (left panel, BoM, panels editor,
// roof-level fields, env config), add its constant below. Missing an action
// here means the user's change won't autosave until something else dirties
// the state or they exit the page.
//
// Excluded by design (all bootstrap/internal noise that mutates the same
// slices but doesn't represent a user-saved change):
//   - ADD_ROOF: merely marking a roof outline has zero BOM impact and the
//     roof carries no persisted-meaningful content until panels are added.
//     Dirtying here triggered a spurious autosave PUT and the BomPanel
//     "Updating BOM…" overlay every time the user marked a new roof. The
//     roof IS captured by the next save because ADD_PANELS dirties the state
//     and buildLiteUpdatePayload serializes every roof in drawingManager.
//   - LOAD_ROOFS_FROM_API, LOAD_PROJECT_CONFIGURATION, LOAD_PANELS,
//     LOAD_PANELS_FROM_HISTORY, ADD_PANEL_BAY_MAPPING, RESET_*, CLEAR_*
//   - SAVE_PANELS_TO_DRAWING_MANAGER (fires inside maybeAutoSave's own
//     savePanelsEditor() call — including it would re-mark dirty mid-save)
//   - ACTIVE_PANELS_EDITOR, DEACTIVE_PANELS_EDITOR, SET_BLANK_MAPS_ROOFS_SELECTOR
//   - SET_PRODUCT_ID, SET_PRODUCT_TITLE (set once at mount)
//   - SET_DRAW_RUBBER_BAND, SHOW_CURSOR, UNBLOCK_CREATING_PANEL (UI plumbing)
import { DrawingManagerActionTypes } from '__editor/googleMapsRoofsSelector/components/drawingManager/drawingManagerActionsConstants';
import { PanelsActionTypes } from '__editor/panelsEditor/components/panels/panelsActionsConstants';
import { TiltedRoofActionTypes } from '__editor/panelsEditor/components/tiltedRoof/tiltedRoofActionsConstants';
import { SettingsActionTypes } from '__editor/panelsEditor/components/settings/settingsActionsConstants';
import { ProjectConfigurationActionTypes } from 'projectDesign/components/projectConfiguration/projectConfigurationActionsConstants';
import { ModuleSelectorActionTypes } from '__common/components/moduleSelector/moduleSelectorActionsConstants';
import { LiteHistoryActionTypes } from 'v3/workspace/history/liteHistoryActionsConstants';
import { MARK_LITE_DIRTY } from 'app/appActions';

const DIRTY_ACTIONS = new Set<string>([
  // Panels editor — drawing/manipulation of panels on the canvas.
  PanelsActionTypes.ADD_PANELS,
  PanelsActionTypes.REQUEST_ADD_PANELS,
  PanelsActionTypes.REMOVE_PANELS,
  PanelsActionTypes.REMOVE_BAYS,
  PanelsActionTypes.END_DRAG_PANEL,
  PanelsActionTypes.UPDATE_PANELS,
  PanelsActionTypes.UPDATE_PANEL_SPACINGS,
  PanelsActionTypes.TOGGLE_PANEL_ATTACHED,
  PanelsActionTypes.TOGGLE_BAY_ATTACHED,
  PanelsActionTypes.CHANGE_DESIRED_TABLE_LENGTH,
  PanelsActionTypes.CHANGE_DESIRED_TABLE_WIDTH,
  PanelsActionTypes.CHANGE_SINGLE_PANEL_ROOF_ZONE,

  // Drawing manager — per-roof state changes.
  DrawingManagerActionTypes.DELETE_ROOF,
  DrawingManagerActionTypes.DELETE_ALL_ROOFS,
  DrawingManagerActionTypes.UPDATE_ONE_ROOF_CORNER,
  DrawingManagerActionTypes.UPDATE_ROOF_COORDS,
  DrawingManagerActionTypes.ADD_POINT_TO_ROOF,
  DrawingManagerActionTypes.SET_ARRAY_NAME,
  DrawingManagerActionTypes.SET_ROOF_ROW_SPACING,
  DrawingManagerActionTypes.SET_ROOF_TABLE_ROW_SPACING,
  DrawingManagerActionTypes.SET_ROOF_ROOF_PITCH,
  DrawingManagerActionTypes.RESIZE_PANELS,
  DrawingManagerActionTypes.REPLACE_PANELS_ON_ROOF,
  DrawingManagerActionTypes.REMOVE_PANELS_ON_ROOF,
  DrawingManagerActionTypes.DELETE_PANELS_INSIDE_OF_ROOF,
  DrawingManagerActionTypes.CHANGE_CHOOSE_BASED_ON_LAYOUT,
  DrawingManagerActionTypes.UPDATE_BLANK_MAP_ROOF_LENGTH_WIDTH_FOR_BLANK_ROOF,

  // Tilted roof — left panel pitch / structure tilt.
  TiltedRoofActionTypes.SET_TILTED_ROOF,
  TiltedRoofActionTypes.SET_TILTED_ROOF_STRUCTURE_TILT,

  // Settings — orientation and spacing controls.
  SettingsActionTypes.DRAW_ORIENTATION_CHANGE,
  SettingsActionTypes.CHANGE_PANELS_SIZES,
  SettingsActionTypes.SET_ROW_SPACING,
  SettingsActionTypes.SET_COLUMN_SPACING,
  SettingsActionTypes.SET_TABLE_ROW_SPACING,

  // Project configuration — left panel / advanced section / env config.
  ProjectConfigurationActionTypes.SET_PROJECT_OPTION,
  ProjectConfigurationActionTypes.SET_ZIP_AND_ADDRESS_CODE,
  ProjectConfigurationActionTypes.SET_ENVIRONMENTAL_FACTORS,
  ProjectConfigurationActionTypes.SET_PROJECT_NAME,
  ProjectConfigurationActionTypes.SET_CITY_NAME,
  ProjectConfigurationActionTypes.SET_RAILS_PRODUCT_ID,
  ProjectConfigurationActionTypes.SET_SPAN,
  ProjectConfigurationActionTypes.SET_INPUT_UNIT,
  ProjectConfigurationActionTypes.TOGGLE_INPUT_UNIT,
  ProjectConfigurationActionTypes.SET_ADDITIONAL_LOAD_COMBINATION,
  ProjectConfigurationActionTypes.SET_DEAD_LOAD_FACTOR_MODIFICATION,
  ProjectConfigurationActionTypes.SET_SEISMIC_DESIGN_CATEGORY,
  ProjectConfigurationActionTypes.CHANGE_STRUCTURE_TYPE,
  ProjectConfigurationActionTypes.CHANGE_STRUCTURE_TYPE_TILT,
  ProjectConfigurationActionTypes.SET_ADJUSTABLE_TILT_SYSTEM,
  ProjectConfigurationActionTypes.CHANGE_STRUCTURE_TYPE_PREFERRED_SPAN,
  ProjectConfigurationActionTypes.SET_SEISMIC_SETBACK_DISTANCE,
  ProjectConfigurationActionTypes.SET_AUTO_APPLY_STATE_BUILDING_CODE,

  // Module selector — BoM / module / manufacturer selection.
  ModuleSelectorActionTypes.SET_MFG,
  ModuleSelectorActionTypes.SET_MODEL,
  ModuleSelectorActionTypes.SET_CUSTOM_VALUE,
  ModuleSelectorActionTypes.SET_SAME_PANEL_DIMS,

  // History — undo / redo. The Lite history reducer reverts the panels
  // slice on these, which IS a persisted user change (the next autosave
  // PUT needs to overwrite the server with the reverted layout). Without
  // these here, user undoes the last panel add but liteDirty stays false,
  // maybeAutoSave skips the PUT, and a refresh still shows the un-undone
  // state from the server.
  LiteHistoryActionTypes.LITE_UNDO_HISTORY,
  LiteHistoryActionTypes.LITE_REDO_HISTORY,
]);

export const liteDirtyMiddleware = (store: any) => (next: any) => (action: any) => {
  const result = next(action);
  if (!action || typeof action.type !== 'string') return result;
  const state = store.getState();
  if (!(state.app && state.app.isUbuilderLite)) return result;
  // Ignore ALL load-time dirtying until the user's first interaction. Loading a
  // project fires many whitelisted mutations asynchronously AFTER the editor is
  // ready — the models API fill-in (SET_MFG/SET_MODEL) and the panels-editor
  // mount hydration (SET_ROW_SPACING / SET_TABLE_ROW_SPACING / SET_ROOF_ROW_SPACING,
  // panel resizes, etc.). None represent a user change, but they'd dirty the
  // untouched project and make the 5s autosave PUT it back (even without
  // ?regen_bom). Pre-interaction nothing is genuinely user-edited (the baseline
  // is already persisted), so suppressing here is safe. UBuilderLiteContainer
  // clears the flag on the user's first interaction.
  if (state.app.liteSuppressDirty) {
    return result;
  }
  if (DIRTY_ACTIONS.has(action.type) && !state.app.liteDirty) {
    store.dispatch(MARK_LITE_DIRTY());
  }
  return result;
};
